import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";
import {
  ChevronDown,
  Star,
  Info,
  Search,
  Filter,
  X,
  Check,
} from "lucide-react";
import { useGetMatchByIdQuery } from "../../../store/apis";
import { useDispatch, useSelector } from "react-redux";
import { addBet, removeBet } from "../../../store/betslipold-slice";
import { toastService } from "../../notificationPage/ToastContainer";
import DetailNavbar from "./DetailNavbar";
import MatchProfileSkeleton, {
  MatchProfileSkeletonClean,
} from "./MatchProfileSkeleton";

const MatchProfile = ({ matchId, isDarkMode = false }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedMarkets, setExpandedMarkets] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const searchInputRef = useRef(null);

  const dispatch = useDispatch();
  const bets = useSelector((state) => state.betSlip.bets);

  const { data, isLoading, error } = useGetMatchByIdQuery(matchId);

  // Function to toggle market expansion
  const toggleMarketExpansion = useCallback((marketName) => {
    setExpandedMarkets((prev) =>
      prev.includes(marketName)
        ? prev.filter((m) => m !== marketName)
        : [...prev, marketName]
    );
  }, []);

  // Function to get trading status for a market
  const getMarketTradingStatus = useCallback((oddsArray) => {
    const tradingStatusEntry = oddsArray.find(
      (odd) => odd.label === "TradingStatus"
    );
    return tradingStatusEntry ? tradingStatusEntry.value : "Open";
  }, []);

  // Function to convert new API structure to old format with trading status and selectionId
  const convertApiDataToMarkets = useCallback(() => {
    if (
      !data ||
      !data.events ||
      !Array.isArray(data.events) ||
      !data.events[0] ||
      !data.events[0].odds
    ) {
      return [];
    }

    const event = data.events[0];
    const markets = [];

    // Convert odds object to array of markets
    Object.entries(event.odds).forEach(([marketName, oddsArray]) => {
      // Get trading status for this market
      const tradingStatus = getMarketTradingStatus(oddsArray);

      // Filter out TradingStatus entries and convert to market format
      const marketOdds = oddsArray
        .filter((odd) => odd.label !== "TradingStatus")
        .map((odd, index) => ({
          label: odd.label,
          value: odd.value,
          highlight: false, // You can add logic here for highlighting
          tradingStatus: tradingStatus, // Add trading status to each odd
          // Generate selectionId - this should ideally come from your API
          selectionId:
            odd.selectionId || `${matchId}_${marketName}_${odd.label}_${index}`,
          oddId: odd.oddId || `${matchId}_${marketName}_${odd.label}`,
        }));

      if (marketOdds.length > 0) {
        markets.push({
          marketName,
          marketOdds,
          tradingStatus: tradingStatus, // Add trading status to market
          description: null, // Add descriptions if available in your data
          marketId: `${matchId}_${marketName}`,
        });
      }
    });

    return markets;
  }, [data, getMarketTradingStatus, matchId]);

  // Function to filter markets by category and search term
  const getFilteredMarkets = useCallback(() => {
    const markets = convertApiDataToMarkets();

    let filteredMarkets = [...markets];

    // Filter by category
    if (activeCategory !== "All") {
      const categoryMap = {
        Popular: [
          "Both Teams To Score",
          "Correct Score",
          "Total Goals (Bands)",
          "Team To Score",
        ],
        Match: [
          "Match Result",
          "Double Chance",
          "Draw No Bet",
          "Half Time/Full Time",
          "Highest Scoring Half",
          "To Qualify",
        ],
        Goals: [
          "Total Goals (Bands)",
          "Both Teams To Score",
          "Both Teams to Score 2 or More Goals Yes/No",
          "Away Team To Score Yes/No",
          "Both Teams to Score in Both Halves",
          "Second Half Home Team Total Goals Odd Or Even",
        ],
        Handicap: ["Handicap", "Asian Handicap"],
        Player: ["First Goalscorer", "Anytime Goalscorer"],
        Specials: [
          "Correct Score",
          "Away Team to Win Either Half Yes/No",
          "Both Teams to Score in Both Halves",
        ],
      };

      const categoryMarkets = categoryMap[activeCategory] || [];
      filteredMarkets = filteredMarkets.filter((market) =>
        categoryMarkets.includes(market.marketName)
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filteredMarkets = filteredMarkets.filter(
        (market) =>
          market.marketName.toLowerCase().includes(term) ||
          market.marketOdds.some((odd) =>
            odd.label.toLowerCase().includes(term)
          )
      );
    }

    return filteredMarkets;
  }, [convertApiDataToMarkets, activeCategory, searchTerm]);

  // Enhanced selection check using selectionId when available
  const isOddSelected = useCallback(
    (odd, market) => {
      if (!data || !data.events) return false;

      // First try to match by selectionId (most reliable)
      if (odd.selectionId) {
        return bets.some((bet) => bet.selectionId === odd.selectionId);
      }

      // Fallback to the original method
      return bets.some(
        (bet) =>
          bet.match.id === matchId &&
          bet.odd.label === odd.label &&
          bet.market?.marketName === market.marketName
      );
    },
    [bets, matchId, data]
  );

  // Get visual state for an odd button
  const getOddButtonState = useCallback(
    (odd, market) => {
      const isSelected = isOddSelected(odd, market);
      const tradingStatus = market.tradingStatus || "Open";

      return {
        isSelected,
        isDisabled: tradingStatus !== "Open",
        isSuspended: tradingStatus === "Suspended",
        isClosed: tradingStatus === "Closed",
        tradingStatus,
      };
    },
    [isOddSelected]
  );

  // Enhanced odd selection with proper selectionId handling
  const handleOddSelect = useCallback(
    (odd, market) => {
      if (!data || !data.events || !data.events[0]) return;

      // Check if market is available for betting
      if (market.tradingStatus !== "Open") {
        const statusMessage =
          market.tradingStatus === "Suspended"
            ? "Market is currently suspended"
            : "Market is closed for betting";
        toastService.error(statusMessage);
        return;
      }

      const event = data.events[0];

      // Create enhanced match data
      const matchData = {
        id: matchId,
        gameName: event.gameName, // Updated to match expected field name
        name: event.gameName,
        date: event.eventDate,
        startTime: event.eventDate,
      };

      // Create market data with enhanced properties
      const marketData = {
        marketName: market.marketName,
        tradingStatus: market.tradingStatus || "Open",
        marketId: market.marketId || `${matchId}_${market.marketName}`,
      };

      // Enhanced odd data with all required fields
      const enhancedOdd = {
        ...odd,
        label: odd.label,
        value: odd.value,
        originalValue: odd.originalValue || odd.value,
        currentValue: odd.value,
        tradingStatus: odd.tradingStatus || market.tradingStatus || "Open",
        oddId: odd.oddId || `${matchId}_${market.marketName}_${odd.label}`,
        selectionId:
          odd.selectionId || `${matchId}_${market.marketName}_${odd.label}`,
      };

      const betExists = isOddSelected(odd, market);

      // Check if any bet exists for this match
      const hasAnyBetForMatch = bets.some((bet) => {
        return bet.match.id === matchId;
      });

      // Handle different scenarios
      if (betExists) {
        // Remove the bet if it's already selected using selectionId
        const existingBetToRemove = bets.find(
          (bet) =>
            bet.selectionId === enhancedOdd.selectionId ||
            (bet.match.id === matchId &&
              bet.odd.label === enhancedOdd.label &&
              bet.market?.marketName === marketData.marketName)
        );

        if (existingBetToRemove) {
          dispatch(
            removeBet({
              selectionId: existingBetToRemove.selectionId,
              match: matchData,
              odd: enhancedOdd,
              market: marketData,
            })
          );
          toastService.warning(
            `Removed: ${odd.label} from ${market.marketName}`
          );
        }
      } else if (hasAnyBetForMatch) {
        // Remove all existing bets for this match
        const existingBets = bets.filter((bet) => bet.match.id === matchId);
        existingBets.forEach((bet) => {
          dispatch(
            removeBet({
              selectionId: bet.selectionId,
              match: bet.match,
              odd: bet.odd,
              market: bet.market,
            })
          );
        });

        // Add the new bet with enhanced data
        dispatch(
          addBet({
            match: matchData,
            odd: enhancedOdd,
            market: marketData,
            selectionId: enhancedOdd.selectionId,
          })
        );
        toastService.info(
          `Changed selection to: ${odd.label} in ${market.marketName}`
        );
      } else {
        // Simply add the bet if no other bets exist for this match
        dispatch(
          addBet({
            match: matchData,
            odd: enhancedOdd,
            market: marketData,
            selectionId: enhancedOdd.selectionId,
          })
        );
        toastService.success(`Added: ${odd.label} @ ${odd.value}`);
      }
    },
    [bets, data, dispatch, isOddSelected, matchId]
  );

  // Render team information
  const renderTeamHeader = useCallback(() => {
    if (!data || !data.events || !data.events[0]) return null;

    const event = data.events[0];
    const gameNameParts = event.gameName.split(" v ");
    const homeTeam = gameNameParts[0];
    const awayTeam = gameNameParts[1];

    return (
      <div className="flex justify-between items-center mt-4">
        <div className="text-center flex-1">
          <h3 className="font-bold text-lg text-darkblue dark:text-white">
            {homeTeam}
          </h3>
        </div>
        <div className="text-xl font-bold mx-4 text-darkblue dark:text-white">
          VS
        </div>
        <div className="text-center flex-1">
          <h3 className="font-bold text-lg text-darkblue dark:text-white">
            {awayTeam}
          </h3>
        </div>
      </div>
    );
  }, [data]);

  // Render market status indicator
  const renderMarketStatusIndicator = useCallback((tradingStatus) => {
    if (tradingStatus === "Open") return null;

    const statusConfig = {
      Suspended: {
        color: "bg-yellow-500",
        text: "text-yellow-800",
        bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
        label: "SUSPENDED",
      },
      Closed: {
        color: "bg-red-500",
        text: "text-red-800",
        bgColor: "bg-red-50 dark:bg-red-900/20",
        label: "CLOSED",
      },
    };

    const config = statusConfig[tradingStatus];
    if (!config) return null;

    return (
      <div
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.text}`}
      >
        <div className={`w-2 h-2 rounded-full ${config.color} mr-1.5`}></div>
        {config.label}
      </div>
    );
  }, []);

  // Enhanced styling for odd buttons
  const getOddButtonStyling = useCallback((odd, market, buttonState) => {
    const { isSelected, isDisabled, isSuspended, isClosed } = buttonState;

    let baseClasses =
      "rounded-md relative transition-all duration-300 transform cursor-pointer";

    if (isDisabled) {
      return `${baseClasses} opacity-50 cursor-not-allowed bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 scale-95`;
    }

    if (isSelected) {
      return `${baseClasses} 
        bg-gradient-to-br from-yellow-400 to-yellow-500 
        dark:from-yellow-500 dark:to-yellow-600
        border-yellow-600 ring-2 ring-yellow-400 ring-opacity-75
        shadow-lg shadow-yellow-400/50 scale-105
        before:absolute before:inset-0 before:bg-white before:opacity-20 before:rounded-md`;
    }

    if (isSuspended) {
      return `${baseClasses} 
        bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700
        hover:bg-yellow-100 dark:hover:bg-yellow-900/30 hover:scale-102`;
    }

    return `${baseClasses} 
      bg-[#F3F4F6] dark:bg-[#2F425C] border-[#E5E7EB] dark:border-doubleextralightwhite
      hover:bg-yellow-50 dark:hover:bg-yellow-900/20 hover:border-yellow-300 dark:hover:border-yellow-600
      hover:shadow-md hover:scale-102 hover:ring-1 hover:ring-yellow-300 hover:ring-opacity-50`;
  }, []);

  // Get label styling based on selection state
  const getLabelStyling = useCallback((buttonState) => {
    const { isSelected, isDisabled } = buttonState;
    let baseClasses = "text-xs font-medium transition-colors duration-300";

    if (isDisabled) {
      return `${baseClasses} text-gray-400 dark:text-gray-500`;
    }

    if (isSelected) {
      return `${baseClasses} text-black font-semibold`;
    }

    return `${baseClasses} text-[#A3A3A3] dark:text-extralightwhite`;
  }, []);

  // Get value styling based on selection state
  const getValueStyling = useCallback((buttonState) => {
    const { isSelected, isDisabled } = buttonState;
    let baseClasses = "text-sm font-semibold transition-colors duration-300";

    if (isDisabled) {
      return `${baseClasses} text-gray-400 dark:text-gray-500`;
    }

    if (isSelected) {
      return `${baseClasses} text-black font-bold`;
    }

    return `${baseClasses} text-mutedblue dark:text-white`;
  }, []);

  // Render market and odds with enhanced selection UI and trading status
  const renderMarketOdds = useCallback(
    (market) => {
      const isExpanded = expandedMarkets.includes(market.marketName);
      const gridColumns =
        market.marketOdds.length <= 3
          ? market.marketOdds.length
          : market.marketOdds.length <= 6
          ? 3
          : 4;

      return (
        <div
          key={market.marketName}
          className={`${market.tradingStatus !== "Open" ? "opacity-75" : ""}`}
        >
          {/* Market Header */}
          <div
            onClick={() => toggleMarketExpansion(market.marketName)}
            className="w-full relative flex justify-between items-center cursor-pointer max-sm:p-[10px_15px] p-[10px_23px] bg-bglightblue dark:bg-darktitleColor"
          >
            <div className="flex items-center gap-2">
              <span className="text-[#404040] dark:text-white max-sm:text-xs text-sm font-medium leading-[128%]">
                {market.marketName}
              </span>
              {renderMarketStatusIndicator(market.tradingStatus)}
            </div>

            {market.description && (
              <div className="group relative" title={market.description}>
                <Info
                  size={16}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                />
              </div>
            )}

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {market.marketOdds.length} selections
              </span>
              <ChevronDown
                className={`transform transition-transform ${
                  isExpanded ? "rotate-180" : ""
                } text-gray-600 dark:text-gray-300`}
              />
            </div>
          </div>

          {/* Enhanced Market Odds Grid */}
          {isExpanded && (
            <div className={`grid grid-cols-${gridColumns} gap-3 p-4`}>
              {market.marketOdds.map((odd, oddIndex) => {
                const buttonState = getOddButtonState(odd, market);
                const { isSelected, isDisabled, isSuspended, isClosed } =
                  buttonState;

                return (
                  <div
                    key={oddIndex}
                    className={`relative ${getOddButtonStyling(
                      odd,
                      market,
                      buttonState
                    )} border`}
                    onClick={() => !isDisabled && handleOddSelect(odd, market)}
                  >
                    {/* Selected Indicator */}
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 z-10">
                        <div className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg">
                          <Check size={12} />
                        </div>
                      </div>
                    )}

                    {/* Trading Status Indicators */}
                    {isSuspended && (
                      <div className="absolute top-1 left-1 z-10">
                        <div
                          className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"
                          title="Suspended"
                        ></div>
                      </div>
                    )}

                    {isClosed && (
                      <div className="absolute top-1 left-1 z-10">
                        <div
                          className="w-3 h-3 bg-red-500 rounded-full"
                          title="Closed"
                        ></div>
                      </div>
                    )}

                    <div className="p-3 text-center space-y-2">
                      <div className="flex items-center justify-center space-x-1">
                        <span className={getLabelStyling(buttonState)}>
                          {odd.label}
                        </span>
                        {odd.highlight && !isDisabled && (
                          <span className="ml-1 w-0 h-0 border-b-[6px] border-b-green-500 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent" />
                        )}
                      </div>

                      <div className={getValueStyling(buttonState)}>
                        {isDisabled ? "--" : odd.value}
                      </div>
                    </div>

                    {/* Pulse animation for newly selected items */}
                    {isSelected && (
                      <div className="absolute inset-0 rounded-md animate-pulse bg-yellow-400 opacity-20 pointer-events-none"></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    },
    [
      expandedMarkets,
      handleOddSelect,
      getOddButtonState,
      toggleMarketExpansion,
      renderMarketStatusIndicator,
      getOddButtonStyling,
      getLabelStyling,
      getValueStyling,
    ]
  );

  // Determine the most popular markets to auto-expand
  const popularMarkets = useMemo(
    () => [
      "Both Teams To Score",
      "Correct Score",
      "Total Goals (Bands)",
      "Match Results",
      "Double Chance",
      "Either Team To Win Yes/No",
    ],
    []
  );

  // Auto-expand popular markets on initial render
  useEffect(() => {
    const markets = convertApiDataToMarkets();
    if (markets.length > 0) {
      const marketsToExpand = markets
        .filter((market) => popularMarkets.includes(market.marketName))
        .map((market) => market.marketName);

      setExpandedMarkets(marketsToExpand);
    }
  }, [data, popularMarkets, convertApiDataToMarkets]);

  // Handle keyboard shortcuts for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+F or Command+F to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (isLoading) {
    return <MatchProfileSkeletonClean isDarkMode={isDarkMode} />;
  }

  if (error)
    return (
      <div className="p-6 text-center text-red-500">
        Error loading match data
      </div>
    );

  if (!data || !data.events || data.events.length === 0)
    return <div className="p-6 text-center">No match data available</div>;

  const event = data.events[0];
  const eventDate = event.eventDate;
  const gameName = event.gameName;

  return (
    <div className="max-sm:overflow-hidden max-sm:mt-[5px] mt-3 s18:mt-[17px] rounded-[7px] bg-white dark:bg-bluedarkmodecolor shadow-dashboardshadow relative">
      {/* Match Header */}
      {renderTeamHeader()}

      {/* Market Categories */}
      <DetailNavbar
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
      />

      {/* Markets and Odds */}
      <div className="bg-white dark:bg-bluedarkmodecolor shadow-dashboardshadow sm:!rounded-[0_0_12px_12px]">
        {getFilteredMarkets().length > 0 ? (
          getFilteredMarkets().map(renderMarketOdds)
        ) : (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
            No markets found matching your criteria
          </div>
        )}
      </div>
    </div>
  );
};

// PropTypes for type checking
MatchProfile.propTypes = {
  matchId: PropTypes.string.isRequired,
  isDarkMode: PropTypes.bool,
};

export default MatchProfile;
